import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { platformApiFetch } from '../api/client';

const PlatformAuthContext = createContext(null);

export function PlatformAuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshAdmin = useCallback(async () => {
    try {
      const profile = await platformApiFetch('/api/platform/auth/me');
      setAdmin(profile);
      return profile;
    } catch {
      setAdmin(null);
      return null;
    }
  }, []);

  useEffect(() => {
    refreshAdmin().finally(() => setLoading(false));
  }, [refreshAdmin]);

  const login = useCallback(async (email, password) => {
    const result = await platformApiFetch('/api/platform/auth/login', {
      method: 'POST',
      body: { email, password },
      errorMessage: 'Login failed',
    });
    setAdmin(result.admin);
    return result.admin;
  }, []);

  const logout = useCallback(async () => {
    try {
      await platformApiFetch('/api/platform/auth/logout', { method: 'POST', parseJson: false });
    } finally {
      setAdmin(null);
    }
  }, []);

  const value = useMemo(
    () => ({
      admin,
      loading,
      isAuthenticated: Boolean(admin),
      login,
      logout,
      refreshAdmin,
    }),
    [admin, loading, login, logout, refreshAdmin],
  );

  return <PlatformAuthContext.Provider value={value}>{children}</PlatformAuthContext.Provider>;
}

export function usePlatformAuth() {
  const context = useContext(PlatformAuthContext);
  if (!context) {
    throw new Error('usePlatformAuth must be used within PlatformAuthProvider');
  }
  return context;
}
